import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface PhotoCategory {
  id: number;
  project_id: number;
  name: string;
  sort_order: number;
  created_at: string;
  photo_count: number;
}

export interface Photo {
  id: number;
  project_id: number;
  category_id: number | null;
  category_name: string | null;
  filename: string;
  original_name: string | null;
  size_bytes: number;
  width: number | null;
  height: number | null;
  caption: string | null;
  uploaded_by: number | null;
  uploaded_at: string;
  uploader_name: string | null;
  uploader_abbr: string | null;
}

const keys = {
  categories: (projectId: number) => ['photo-categories', projectId] as const,
  photos: (projectId: number, categoryId?: number | null) =>
    ['photos', projectId, categoryId ?? 'all'] as const,
};

// ── Kategorien ─────────────────────────────────────────────────────

export function usePhotoCategories(projectId: number | undefined) {
  return useQuery({
    queryKey: projectId ? keys.categories(projectId) : ['photo-categories', 'none'],
    queryFn: async () => {
      const { data } = await api.get<{ items: PhotoCategory[] }>(
        `/projects/${projectId}/photo-categories`,
      );
      return data.items;
    },
    enabled: projectId != null,
  });
}

export function useCreatePhotoCategory(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.post<PhotoCategory>(
        `/projects/${projectId}/photo-categories`,
        { name },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.categories(projectId) });
    },
  });
}

export function useDeletePhotoCategory(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId: number) => {
      await api.delete(`/photo-categories/${categoryId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.categories(projectId) });
    },
  });
}

// ── Fotos ──────────────────────────────────────────────────────────

export function useProjectPhotos(
  projectId: number | undefined,
  categoryId?: number | null,
) {
  return useQuery({
    queryKey: projectId
      ? keys.photos(projectId, categoryId)
      : ['photos', 'none'],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryId !== undefined && categoryId !== null) {
        params.set('category_id', String(categoryId));
      } else if (categoryId === null) {
        params.set('category_id', 'null');
      }
      const qs = params.toString();
      const { data } = await api.get<{ items: Photo[] }>(
        `/projects/${projectId}/photos${qs ? '?' + qs : ''}`,
      );
      return data.items;
    },
    enabled: projectId != null,
  });
}

export interface UploadPhotoInput {
  blob: Blob;
  originalName: string;
  categoryId: number | null;
  caption?: string | null;
  width?: number;
  height?: number;
}

export function useUploadPhoto(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadPhotoInput) => {
      const form = new FormData();
      // Stored-Name bekommt der Server; das hier ist nur was multer
      // als file.originalname sieht. Wir behalten die ursprüngliche
      // Endung des Phone-Files für Audit/Anzeige.
      form.append('file', input.blob, input.originalName);
      form.append('category_id', input.categoryId == null ? 'null' : String(input.categoryId));
      if (input.caption) form.append('caption', input.caption);
      if (input.width) form.append('width', String(input.width));
      if (input.height) form.append('height', String(input.height));
      const { data } = await api.post<Photo>(`/projects/${projectId}/photos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos', projectId] });
      qc.invalidateQueries({ queryKey: keys.categories(projectId) });
    },
  });
}

export function useDeletePhoto(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: number) => {
      await api.delete(`/photos/${photoId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos', projectId] });
      qc.invalidateQueries({ queryKey: keys.categories(projectId) });
    },
  });
}

export interface UpdatePhotoInput {
  category_id?: number | null;
  caption?: string | null;
}

export function useUpdatePhoto(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: number; patch: UpdatePhotoInput }) => {
      const { data } = await api.patch<Photo>(`/photos/${args.id}`, args.patch);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photos', projectId] });
      qc.invalidateQueries({ queryKey: keys.categories(projectId) });
    },
  });
}

/** URL zum Foto-Bild für <img src=…>. */
export function photoUrl(photoId: number): string {
  return `/api/v1/photos/${photoId}/file`;
}
